import { inject, Injectable } from '@angular/core';

import { type RuntimeContext } from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { type CopyToClipboardWorkflowNode } from '@blaze-shared/workflow-domain';

import { normalizeNodeInput } from '../../node-input-rows';

/**
 * Copies text to the clipboard — one line per input row, taken from a column or from an expression.
 *
 * @returns the text that was copied, which is empty when there was nothing to copy.
 */
@Injectable({ providedIn: 'root' })
export class CopyToClipboardActionService {
  private readonly iss = inject(IntelliSenseService);
  private readonly notificationService = inject(NotificationService);
  private readonly schemaService = inject(SchemaService);

  copyToClipboard(node: CopyToClipboardWorkflowNode, nodeInput: any, ctx: RuntimeContext): string {
    const condition = node.condition || '';
    const columnName = this.schemaService.replaceColumnIdsWithNames(node.column || '');

    const finalText = normalizeNodeInput(nodeInput)
      .map((row) => {
        let context = this.iss.updateRuntimeContext(ctx, row);
        context = this.iss.updateRuntimeContextWithStoredVarsDefaults(context, node.nodeType);
        if (columnName) {
          return row[columnName] ?? '';
        }
        return condition ? this.iss.eval(condition, context) : '';
      })
      .join('\n');

    if (finalText) {
      navigator.clipboard.writeText(finalText);
      this.notificationService.success(`Copied!`, true);
    }
    return finalText;
  }
}
